import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { ArClaim } from '../types';
import { lateReasonOf, money } from '../utils';
import { logHrefFor } from '../logLinks';

interface Props {
  claim: ArClaim;
  onStop: () => void;
  /** Opens this claim's Human in the loop item (carries the id, never the first). */
  onOpenHitl: (itemId: string) => void;
  onChangeLimit: () => void;
  /** "Resume follow-up" on a paused / stopped claim. */
  onResume: () => void;
  onBack?: () => void;
}

function StatusCard({ claim, onOpenHitl, onResume }: { claim: ArClaim; onOpenHitl: (itemId: string) => void; onResume: () => void }) {
  const s = claim.status;

  // ELVA isn't following up (Oct 22): plain cards. Paused and "Something else" can be resumed.
  if (s.kind === 'paused' || s.kind === 'billed_elsewhere' || s.kind === 'other') {
    return (
      <>
        <section className="iar-card iar-card-pad" aria-labelledby="iar-st" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>
            <span className="iar-badge b-mute">{s.kind === 'paused' ? 'Paused' : 'Not following up'}</span>
          </div>
          <strong id="iar-st">{s.headline}</strong>
          {s.bodyLines?.map((line, i) => (
            <div key={`b-${String(i)}`} style={{ fontSize: 14.5, color: 'var(--fg2)' }}>{line}</div>
          ))}
          {s.kind !== 'billed_elsewhere' ? (
            <div>
              <button type="button" className="iar-btn" onClick={onResume}>Resume follow-up</button>
            </div>
          ) : null}
        </section>
        <p className="iar-meta">Still owed: it counts in what insurers owe you.</p>
      </>
    );
  }

  if (s.kind === 'ontime') {
    return (
      <>
        <section className="iar-card iar-card-pad" aria-labelledby="iar-st" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <strong id="iar-st">{s.headline}</strong>
          {s.bodyLines?.map((line, i) => (
            <div key={`b-${String(i)}`} style={{ fontSize: 14.5, color: 'var(--fg2)' }}>{line}</div>
          ))}
        </section>
        <p className="iar-meta">Nothing for anyone to do.</p>
      </>
    );
  }

  if (s.kind === 'pays-patient') {
    return (
      <>
        <section className="iar-card iar-card-pad" aria-labelledby="iar-st" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div><span className="iar-badge b-mute">Pays the patient</span></div>
          <strong id="iar-st">{s.headline}</strong>
          {s.latest ? <div style={{ fontSize: 14.5, color: 'var(--fg2)' }}><strong>Latest:</strong> {s.latest}</div> : null}
          {s.bodyLines?.map((line, i) => (
            <div key={`b-${String(i)}`} style={{ fontSize: 14.5, color: 'var(--fg2)' }}>{line}</div>
          ))}
        </section>
        <p className="iar-meta">Nothing for anyone to do. Not counted in what insurers owe you.</p>
      </>
    );
  }

  const why = claim.group === 'late' ? lateReasonOf(claim) : null;
  const tone = s.kind === 'handled' ? 'info' : s.kind === 'paid' ? 'ok' : 'warn';
  const badge =
    s.kind === 'handled' ? 'ELVA handled it'
      : s.kind === 'limit' ? 'Needs a person'
        : s.kind === 'paid' ? 'Paid'
          : 'Late';
  const badgeClass = s.kind === 'handled' ? 'b-info' : s.kind === 'paid' ? 'b-ok' : s.kind === 'limit' ? 'b-bad' : 'b-warn';

  return (
    <section className={`iar-card iar-status ${tone}`} aria-labelledby="iar-st">
      <div className="iar-status-head">
        <span className={`iar-badge ${badgeClass}`} style={{ background: '#fff' }}>{badge}</span>
        <h3 id="iar-st" className="iar-status-title">{s.headline}</h3>
      </div>
      <div className="iar-status-body">
        {why ? <div><strong>Why it’s late:</strong> {why.line}</div> : null}
        {s.latest ? <div><strong>Latest:</strong> {s.latest}</div> : null}
        {s.next ? <div><strong>Next:</strong> {s.next}</div> : null}
        {s.bodyLines?.map((line, i) => <div key={`b-${String(i)}`}>{line}</div>)}
        {claim.linkedLine ? <div>{claim.linkedLine}</div> : null}
        {claim.hitlItemId ? (
          <div style={{ marginTop: 4 }}>
            <button type="button" className="iar-btn" onClick={() => { onOpenHitl(claim.hitlItemId ?? ''); }}>Open in Human in the loop</button>
          </div>
        ) : null}
        {s.deadline ? <div className="iar-meta">{s.deadline}</div> : null}
      </div>
    </section>
  );
}

export function Detail({ claim, onStop, onOpenHitl, onChangeLimit, onResume, onBack }: Props) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [claim.id]);

  return (
    <main className="iar-detail">
      {onBack ? <button type="button" className="iar-back" onClick={onBack}>← All claims</button> : null}
      <div className="iar-detail-inner">
        {/* 1 · Who */}
        <section className="iar-who" aria-labelledby="iar-who-h">
          <div>
            <h2 id="iar-who-h" className="iar-patient" tabIndex={-1} ref={headingRef}>{claim.patient}</h2>
            <div className="iar-chsub">{claim.payer.name} · member {claim.memberId} · {claim.dentist}{claim.group === 'pays-patient' ? ` · out of network with ${claim.payer.short}` : ''}</div>
            <div className="iar-chmeta">{claim.procedure} · {claim.visit} · {claim.group === 'pays-patient' ? `claim sent ${claim.sentDisplay}` : `sent ${claim.sentDisplay} · ${String(claim.serviceDays ?? claim.days)} days by date of service · ${String(claim.days)} days since sent`} · {claim.receivedDisplay}</div>
          </div>
          <div className="iar-right">
            <div className="iar-meta">Billed</div>
            <div className="iar-bigfig">{money(claim.billed)}</div>
          </div>
        </section>

        {/* 2 · Status */}
        <StatusCard claim={claim} onOpenHitl={onOpenHitl} onResume={onResume} />

        {/* 3 · What ELVA has done */}
        <section className="iar-card iar-status" aria-labelledby="iar-fh">
          <div style={{ padding: '16px 18px 8px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
            <h3 id="iar-fh" className="iar-h3">What ELVA has done</h3>
            {claim.keptGoing ? (
              <span className="iar-meta">
                Follow-up {claim.ran ?? claim.round?.n ?? 0} · past your limit of {claim.keptGoing.limit}, kept going by {claim.keptGoing.by} on {claim.keptGoing.on} · <button type="button" className="iar-src" onClick={onChangeLimit}>Change the limit</button>
              </span>
            ) : claim.round ? (
              <span className="iar-meta">Follow-up {Math.min(claim.round.n, claim.round.limit)} of {claim.round.limit} · <button type="button" className="iar-src" onClick={onChangeLimit}>Change the limit</button></span>
            ) : null}
          </div>
          {claim.tries.map((t, i) => (
            <div key={`try-${String(i)}`} className="iar-try">
              <span className="t">{t.date}</span>
              <span className="iar-try-main">
                <span className={`iar-ic ${t.icon}`} aria-hidden="true">{t.icon === 'ok' ? '✓' : t.icon === 'x' ? '×' : '·'}</span>
                <span><strong>{t.head}</strong> {t.body}</span>
              </span>
              <span className="iar-try-links">
                {t.recordingId ? (
                  <Link className="iar-src" to={logHrefFor(claim.payer.name, t)}>Recording</Link>
                ) : null}
                <Link className="iar-src" to={logHrefFor(claim.payer.name, t)}>Log</Link>
              </span>
            </div>
          ))}
        </section>

        {/* 4 · Stop following up */}
        {claim.group !== 'not-followed' ? (
          <div className="iar-card iar-stopbar">
            <button type="button" className="iar-btn" onClick={onStop}>Stop following up</button>
            <span className="iar-meta">For when the practice wants ELVA to stop, for a while or for good. You’ll be asked why.</span>
          </div>
        ) : null}

        {/* 5 · History */}
        <p className="iar-history">{claim.history}</p>
      </div>
    </main>
  );
}
